import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { useGetQuickTextTemplatesQuery } from "app/store/quickTextTemplates/api/quickTextTemplateApi";
import { CARDIO_CHECK_ACCOUNTS } from "static/account";
import {
  USER_ROLES,
  CLINIC_FOR_REVIEW,
  CARDIO_CHECKS_CLINIC_NOTE_OPTIONS,
} from "static/constants";
import { getCurrentUserRole } from "utils/currentUser.util";

/**
 * Returns quick text default and user templates for a report. Defaults show for physicians on
 * Clinic Review reports at Cardio Check accounts; archived reports get neither.
 *
 * @param {string} account - The report's account name.
 * @param {{ ownTemplatesOnReviewOnly?: boolean }} [options] - Own templates for Clinic Review reports only.
 * @returns {{ defaultOptions: Array, userOptions: Array, isLoading: boolean, canManageTemplates: boolean, isArchived: boolean }}
 */
const useQuickTextTemplates = (
  account,
  { ownTemplatesOnReviewOnly = false } = {}
) => {
  const { reportCurrentTab, reportDetail } = useSelector(
    (state) => state.transmission
  );
  const { accountsMapper } = useSelector((state) => state.account);
  const { isArchiveReport } = useSelector((state) => state.archiveReport);
  const { shouldRefetch } = useSelector((state) => state.quickTextTemplate);

  const settings = accountsMapper?.[account]?.settings;
  const canManageTemplates = Boolean(settings?.show_quick_text_templates);
  const userRole = getCurrentUserRole();

  const isArchived = isArchiveReport || Boolean(reportDetail?.evk_archived);
  const isReviewReport = reportCurrentTab === CLINIC_FOR_REVIEW;
  const canShowTemplates = Boolean(account) && !isArchived;

  const showDefaults =
    canShowTemplates &&
    Boolean(settings?.show_physician_note_options) &&
    userRole === USER_ROLES.PHYSICIAN &&
    isReviewReport &&
    CARDIO_CHECK_ACCOUNTS.includes(account);

  const showOwnTemplates =
    canShowTemplates &&
    canManageTemplates &&
    (!ownTemplatesOnReviewOnly || isReviewReport);

  const {
    data: userTemplates,
    isLoading,
    refetch,
  } = useGetQuickTextTemplatesQuery(
    {},
    { refetchOnMountOrArgChange: true, skip: !canManageTemplates }
  );

  // Templates changed in another tab.
  useEffect(() => {
    if (shouldRefetch && canManageTemplates) {
      refetch();
    }
  }, [shouldRefetch, canManageTemplates, refetch]);

  const defaultOptions = showDefaults
    ? CARDIO_CHECKS_CLINIC_NOTE_OPTIONS.map((option) => ({
        title: option,
        description: option,
      }))
    : [];

  const userOptions = useMemo(() => {
    if (!showOwnTemplates || !userTemplates) {
      return [];
    }

    return showDefaults
      ? userTemplates.filter(
          ({ title }) => !CARDIO_CHECKS_CLINIC_NOTE_OPTIONS.includes(title)
        )
      : userTemplates;
  }, [showOwnTemplates, showDefaults, userTemplates]);

  return {
    defaultOptions,
    userOptions,
    isLoading: canManageTemplates && isLoading,
    canManageTemplates,
    isArchived,
  };
};

export default useQuickTextTemplates;
